import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Activity,
  RefreshCw,
  LogIn,
  UserPlus,
  MessageSquare,
  BookOpen,
  CreditCard,
  XCircle,
  CheckCircle,
  AlertCircle,
  Trophy,
  Zap,
  Swords,
  ChevronDown,
  ChevronRight,
  Loader2,
  Search,
  X,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface ConversationMessage {
  id: string;
  senderId: string;
  recipientId: string;
  content: string;
  isRead: boolean;
  createdAt: string;
  sender?: ActivityEventUser;
}

interface ActivityEventUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  grade?: number;
  displayName?: string;
  profilePictureUrl?: string;
}

interface ActivityEventItem {
  id: string;
  actorId: string;
  eventType: string;
  targetId?: string;
  subjectType?: string;
  subjectId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  actor: ActivityEventUser;
  target?: ActivityEventUser;
}

type StreamEntry =
  | { kind: "single"; event: ActivityEventItem }
  | { kind: "group"; events: ActivityEventItem[]; actor: ActivityEventUser; groupKey: string };

const EVENT_TYPES = [
  { value: "all", label: "All Events" },
  { value: "user.login", label: "Logins" },
  { value: "user.signup", label: "Signups" },
  { value: "question.attempted", label: "Quiz Attempts" },
  { value: "message.sent", label: "Messages" },
  { value: "subscription.created", label: "Subscriptions" },
  { value: "subscription.cancelled", label: "Cancellations" },
  { value: "challenge.sent", label: "Challenges Sent" },
  { value: "challenge.accepted", label: "Challenges Accepted" },
  { value: "challenge.completed", label: "Challenges Completed" },
];

const TIME_PERIODS = [
  { value: "last_hour",   label: "Last Hour" },
  { value: "today",       label: "Today" },
  { value: "yesterday",   label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_30_days",label: "Last 30 Days" },
  { value: "last_week",   label: "Last Week" },
] as const;

type TimePeriod = typeof TIME_PERIODS[number]["value"];

function getDateRange(period: TimePeriod): { since: Date; until?: Date } {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (period) {
    case "last_hour":
      return { since: new Date(now.getTime() - 60 * 60 * 1000) };
    case "today":
      return { since: startOfToday };
    case "yesterday": {
      const start = new Date(startOfToday);
      start.setDate(start.getDate() - 1);
      const end = new Date(startOfToday.getTime() - 1);
      return { since: start, until: end };
    }
    case "last_7_days":
      return { since: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000) };
    case "last_30_days":
      return { since: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000) };
    case "last_week": {
      // Mon–Sun of last calendar week
      const day = now.getDay(); // 0=Sun
      const daysToLastMon = day === 0 ? 6 : day + 6;
      const lastMon = new Date(startOfToday);
      lastMon.setDate(lastMon.getDate() - daysToLastMon);
      const lastSun = new Date(lastMon);
      lastSun.setDate(lastSun.getDate() + 6);
      lastSun.setHours(23, 59, 59, 999);
      return { since: lastMon, until: lastSun };
    }
  }
}

function groupEvents(events: ActivityEventItem[]): StreamEntry[] {
  const entries: StreamEntry[] = [];

  for (const event of events) {
    if (event.eventType === "question.attempted") {
      const last = entries[entries.length - 1];
      if (
        last &&
        last.kind === "group" &&
        last.actor.id === event.actorId
      ) {
        last.events.push(event);
      } else {
        entries.push({
          kind: "group",
          events: [event],
          actor: event.actor,
          groupKey: `group-${event.id}`,
        });
      }
    } else {
      entries.push({ kind: "single", event });
    }
  }

  return entries;
}

function getEventIcon(eventType: string) {
  switch (eventType) {
    case "user.login":           return <LogIn className="w-4 h-4" />;
    case "user.signup":          return <UserPlus className="w-4 h-4" />;
    case "message.sent":         return <MessageSquare className="w-4 h-4" />;
    case "question.attempted":   return <BookOpen className="w-4 h-4" />;
    case "subscription.created": return <CreditCard className="w-4 h-4" />;
    case "subscription.cancelled": return <XCircle className="w-4 h-4" />;
    case "quiz.completed":       return <Trophy className="w-4 h-4" />;
    case "quiz.started":         return <Zap className="w-4 h-4" />;
    case "challenge.sent":
    case "challenge.accepted":
    case "challenge.completed":  return <Swords className="w-4 h-4" />;
    default:                     return <Activity className="w-4 h-4" />;
  }
}

function getEventColor(eventType: string): string {
  switch (eventType) {
    case "user.login":           return "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
    case "user.signup":          return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";
    case "message.sent":         return "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300";
    case "question.attempted":   return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
    case "subscription.created": return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
    case "subscription.cancelled": return "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";
    case "quiz.completed":       return "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300";
    case "challenge.sent":       return "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300";
    case "challenge.accepted":   return "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300";
    case "challenge.completed":  return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300";
    default:                     return "bg-muted text-muted-foreground";
  }
}

function getEventLabel(eventType: string): string {
  switch (eventType) {
    case "user.login":           return "Login";
    case "user.signup":          return "Signup";
    case "message.sent":         return "Message";
    case "question.attempted":   return "Quiz";
    case "subscription.created": return "Subscribed";
    case "subscription.cancelled": return "Cancelled";
    case "quiz.completed":       return "Quiz Done";
    case "quiz.started":         return "Quiz Start";
    case "challenge.sent":       return "Challenge Sent";
    case "challenge.accepted":   return "Challenge Accepted";
    case "challenge.completed":  return "Challenge Done";
    default:                     return eventType.replace(/\./g, " ");
  }
}

function actorInitials(actor: ActivityEventUser): string {
  return `${actor.firstName?.[0] ?? ""}${actor.lastName?.[0] ?? ""}`.toUpperCase() || "?";
}

function actorName(actor: ActivityEventUser): string {
  return `${actor.firstName} ${actor.lastName}`.trim() || "Unknown";
}

function buildSingleDescription(event: ActivityEventItem): string {
  const meta = event.metadata || {};
  const name = actorName(event.actor);

  switch (event.eventType) {
    case "user.login":   return `${name} logged in`;
    case "user.signup":  return `${name} created an account`;
    case "message.sent": {
      const recipient = (meta.recipientName as string) ||
        (event.target ? actorName(event.target) : "someone");
      return `${name} sent a message to ${recipient}`;
    }
    case "question.attempted": {
      const subject = meta.subject as string;
      const topic = meta.topic as string;
      const isCorrect = meta.isCorrect as boolean;
      const pts = meta.pointsEarned as number;
      let desc = `${name} attempted a question`;
      if (subject) desc += ` in ${subject}`;
      if (topic)   desc += `, ${topic}`;
      if (isCorrect !== undefined) desc += isCorrect ? ` (+${pts ?? 0} pts)` : " (incorrect)";
      return desc;
    }
    case "subscription.created":   return `${name} started a subscription`;
    case "subscription.cancelled": return `${name} cancelled their subscription`;
    case "quiz.completed": {
      const score = meta.score as number;
      const total = meta.total as number;
      return `${name} completed a quiz${score !== undefined ? ` (${score}/${total})` : ""}`;
    }
    case "challenge.sent": {
      const opponent = (meta.opponentName as string) ||
        (event.target ? actorName(event.target) : "someone");
      const subj = meta.subject as string;
      const topic = meta.topic as string;
      return `${name} challenged ${opponent} · ${subj}${topic ? `, ${topic}` : ""}`;
    }
    case "challenge.accepted": {
      const challenger = (meta.challengerName as string) ||
        (event.target ? actorName(event.target) : "someone");
      const subj = meta.subject as string;
      const topic = meta.topic as string;
      return `${name} accepted ${challenger}'s challenge · ${subj}${topic ? `, ${topic}` : ""}`;
    }
    case "challenge.completed": {
      const subj = meta.subject as string;
      const topic = meta.topic as string;
      const cScore = meta.creatorScore as number;
      const oScore = meta.opponentScore as number;
      const cCorrect = meta.creatorCorrect as number;
      const oCorrect = meta.opponentCorrect as number;
      const total = meta.questionCount as number;
      return `${name}: challenge finished · ${subj}${topic ? `, ${topic}` : ""} · ${cCorrect}/${total} vs ${oCorrect}/${total} (${cScore} pts vs ${oScore} pts)`;
    }
    default:
      return `${name}: ${event.eventType}`;
  }
}

function MessageChatPreview({ actorId, targetId }: { actorId: string; targetId: string }) {
  const params = new URLSearchParams({ user1Id: actorId, user2Id: targetId });
  const { data, isLoading } = useQuery<{ messages: ConversationMessage[]; found: boolean }>({
    queryKey: [`/api/admin/messages/between?${params.toString()}`],
    staleTime: 60000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-3 text-muted-foreground text-sm">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading conversation...
      </div>
    );
  }

  if (!data?.found || data.messages.length === 0) {
    return <p className="py-3 text-sm text-muted-foreground">No messages found.</p>;
  }

  return (
    <div className="space-y-2 py-2">
      {data.messages.map((msg) => {
        const isActor = msg.senderId === actorId;
        const senderName = msg.sender
          ? `${msg.sender.firstName} ${msg.sender.lastName}`.trim()
          : "Unknown";
        return (
          <div key={msg.id} className={`flex gap-2 ${isActor ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-md px-3 py-2 text-sm ${isActor ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
              <p className="text-[10px] font-medium opacity-70 mb-0.5">
                {isActor ? "Sender" : senderName}
              </p>
              <p className="break-words">{msg.content}</p>
              <p className="text-[10px] opacity-60 mt-1 text-right">
                {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SingleRow({ event }: { event: ActivityEventItem }) {
  const [expanded, setExpanded] = useState(false);
  const meta = event.metadata || {};
  const isCorrect = meta.isCorrect as boolean | undefined;
  const isMessage = event.eventType === "message.sent";
  const snippet = isMessage
    ? (() => { const c = meta.messageContent as string; return c ? (c.length > 70 ? c.slice(0, 70) + "…" : c) : null; })()
    : null;

  const canExpand = isMessage && !!event.targetId;

  const header = (
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${getEventColor(event.eventType)}`}>
          {getEventIcon(event.eventType)}
          {getEventLabel(event.eventType)}
        </span>

        {event.eventType === "question.attempted" && isCorrect !== undefined && (
          isCorrect
            ? <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
            : <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />
        )}

        <span className="text-sm text-foreground">{buildSingleDescription(event)}</span>

        {canExpand && (
          <span className="ml-auto shrink-0 text-muted-foreground">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>
        )}
      </div>

      {snippet && !expanded && (
        <p className="mt-0.5 text-xs text-muted-foreground truncate">{snippet}</p>
      )}

      <p className="mt-0.5 text-xs text-muted-foreground">
        {event.actor?.email}
        {event.actor?.grade != null && ` · Grade ${event.actor.grade}`}
        {" · "}{formatDistanceToNow(new Date(event.createdAt), { addSuffix: true })}
      </p>
    </div>
  );

  return (
    <div className="py-3">
      {canExpand ? (
        <button
          className="w-full text-left flex gap-3 items-start"
          onClick={() => setExpanded(v => !v)}
        >
          <Avatar className="w-8 h-8 shrink-0 mt-0.5">
            {event.actor?.profilePictureUrl && <AvatarImage src={event.actor.profilePictureUrl} />}
            <AvatarFallback className="text-xs">{actorInitials(event.actor)}</AvatarFallback>
          </Avatar>
          {header}
        </button>
      ) : (
        <div className="flex gap-3 items-start">
          <Avatar className="w-8 h-8 shrink-0 mt-0.5">
            {event.actor?.profilePictureUrl && <AvatarImage src={event.actor.profilePictureUrl} />}
            <AvatarFallback className="text-xs">{actorInitials(event.actor)}</AvatarFallback>
          </Avatar>
          {header}
        </div>
      )}

      {expanded && event.targetId && (
        <div className="mt-2 ml-11 border-l-2 border-border pl-4">
          <MessageChatPreview actorId={event.actorId} targetId={event.targetId} />
        </div>
      )}
    </div>
  );
}

function QuizGroupRow({ group }: { group: Extract<StreamEntry, { kind: "group" }> }) {
  const [expanded, setExpanded] = useState(false);
  const { events, actor } = group;

  const correctCount  = events.filter(e => e.metadata?.isCorrect === true).length;
  const incorrectCount = events.length - correctCount;
  const totalPoints   = events.reduce((sum, e) => sum + ((e.metadata?.pointsEarned as number) || 0), 0);

  const subjects = Array.from(new Set(events.map(e => e.metadata?.subject as string).filter(Boolean)));
  const oldest   = events[events.length - 1];
  const newest   = events[0];

  return (
    <div className="py-3">
      <button
        className="w-full text-left flex gap-3 items-start group"
        onClick={() => setExpanded(v => !v)}
      >
        <Avatar className="w-8 h-8 shrink-0 mt-0.5">
          {actor?.profilePictureUrl && <AvatarImage src={actor.profilePictureUrl} />}
          <AvatarFallback className="text-xs">{actorInitials(actor)}</AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${getEventColor("question.attempted")}`}>
              <BookOpen className="w-4 h-4" />
              Quiz Session
            </span>

            <span className="text-sm font-medium text-foreground">
              {actorName(actor)} answered {events.length} question{events.length !== 1 ? "s" : ""}
            </span>

            <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <CheckCircle className="w-3.5 h-3.5" />
              {correctCount}
            </span>

            {incorrectCount > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
                <AlertCircle className="w-3.5 h-3.5" />
                {incorrectCount}
              </span>
            )}

            {totalPoints > 0 && (
              <span className="text-xs text-muted-foreground">+{totalPoints} pts</span>
            )}

            <span className="ml-auto shrink-0 text-muted-foreground">
              {expanded
                ? <ChevronDown className="w-4 h-4" />
                : <ChevronRight className="w-4 h-4" />
              }
            </span>
          </div>

          <p className="mt-0.5 text-xs text-muted-foreground">
            {actor?.email}
            {actor?.grade != null && ` · Grade ${actor.grade}`}
            {subjects.length > 0 && ` · ${subjects.join(", ")}`}
            {" · "}
            {(() => {
              const start = formatDistanceToNow(new Date(oldest.createdAt), { addSuffix: true });
              const end = formatDistanceToNow(new Date(newest.createdAt), { addSuffix: true });
              return events.length === 1 || start === end ? end : `${start} – ${end}`;
            })()}
          </p>
        </div>
      </button>

      {expanded && (
        <div className="mt-2 ml-11 border-l-2 border-border pl-4 space-y-0">
          {events.map((event, i) => {
            const meta = event.metadata || {};
            const isCorrect = meta.isCorrect as boolean | undefined;
            const pts = meta.pointsEarned as number | undefined;
            const subject = meta.subject as string | undefined;
            const topic = meta.topic as string | undefined;

            return (
              <div key={event.id} className={`flex items-start gap-2 py-2 ${i < events.length - 1 ? "border-b border-border/50" : ""}`}>
                <div className="mt-0.5 shrink-0">
                  {isCorrect === true
                    ? <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                    : isCorrect === false
                    ? <AlertCircle className="w-3.5 h-3.5 text-red-400" />
                    : <BookOpen className="w-3.5 h-3.5 text-muted-foreground" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">
                    {subject && <span className="font-medium">{subject}</span>}
                    {topic && <span className="text-muted-foreground">, {topic}</span>}
                    {!subject && !topic && <span className="text-muted-foreground">Question</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isCorrect === true ? `Correct · +${pts ?? 0} pts` : isCorrect === false ? "Incorrect" : ""}
                    {" · "}
                    {formatDistanceToNow(new Date(event.createdAt), { addSuffix: true })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminActivityStream() {
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("today");
  const [autoRefresh, setAutoRefresh] = useState(true);

  // User search state
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string } | null>(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userSearchRef = useRef<HTMLDivElement>(null);

  // Debounce user search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedUserSearch(userSearch), 300);
    return () => clearTimeout(t);
  }, [userSearch]);

  // Fetch user candidates when searching
  const userSearchParams = new URLSearchParams({ search: debouncedUserSearch, pageSize: "6" });
  const { data: userSearchData, isLoading: userSearchLoading } = useQuery<{ users: any[]; total: number }>({
    queryKey: [`/api/admin/users?${userSearchParams.toString()}`],
    enabled: debouncedUserSearch.length >= 2 && !selectedUser,
  });
  const userCandidates = userSearchData?.users ?? [];

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (userSearchRef.current && !userSearchRef.current.contains(e.target as Node)) {
        setShowUserDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const { since, until } = getDateRange(timePeriod);
  const params = new URLSearchParams({ since: since.toISOString() });
  if (until) params.set("until", until.toISOString());
  if (eventTypeFilter !== "all") params.set("eventType", eventTypeFilter);
  if (selectedUser) params.set("actorId", selectedUser.id);
  const queryKey = [`/api/admin/activity?${params.toString()}`];

  const { data: events = [], isLoading, isFetching, dataUpdatedAt } = useQuery<ActivityEventItem[]>({
    queryKey,
    refetchInterval: autoRefresh ? 15000 : false,
    staleTime: 10000,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [eventTypeFilter, timePeriod, selectedUser]);

  const eventCounts = events.reduce<Record<string, number>>((acc, e) => {
    acc[e.eventType] = (acc[e.eventType] || 0) + 1;
    return acc;
  }, {});

  const streamEntries = groupEvents(events);

  const challengeCount = (eventCounts["challenge.sent"] || 0)
    + (eventCounts["challenge.accepted"] || 0)
    + (eventCounts["challenge.completed"] || 0);

  const summaryTypes = [
    { type: "user.login",           label: "Logins" },
    { type: "user.signup",          label: "Signups" },
    { type: "question.attempted",   label: "Attempts" },
    { type: "message.sent",         label: "Messages" },
    { type: "subscription.created", label: "New Subs" },
    { type: "challenge.sent",       label: "Challenges", overrideCount: challengeCount },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Activity Stream</h1>
          <p className="text-muted-foreground mt-1">Real-time view of student activity and system events.</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(v => !v)}
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${autoRefresh && isFetching ? "animate-spin" : ""}`} />
            {autoRefresh ? "Live" : "Paused"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey })}
            disabled={isFetching}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {summaryTypes.map(({ type, label, overrideCount }: { type: string; label: string; overrideCount?: number }) => (
          <Card
            key={type}
            className={`cursor-pointer hover-elevate ${eventTypeFilter === type ? "ring-2 ring-primary" : ""}`}
            onClick={() => setEventTypeFilter(eventTypeFilter === type ? "all" : type)}
          >
            <CardContent className="p-4 flex flex-col gap-1">
              <div className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium w-fit ${getEventColor(type)}`}>
                {getEventIcon(type)}
                {label}
              </div>
              <span className="text-2xl font-bold">{overrideCount ?? eventCounts[type] ?? 0}</span>
              <span className="text-xs text-muted-foreground">{TIME_PERIODS.find(p => p.value === timePeriod)?.label.toLowerCase()}</span>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-row flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base font-semibold">
              Events
              {events.length > 0 && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  {events.length} loaded
                </span>
              )}
            </CardTitle>

            <div className="flex flex-wrap items-center gap-2">
              {/* User search */}
              <div ref={userSearchRef} className="relative">
                {selectedUser ? (
                  <div className="flex items-center gap-1.5 rounded-md border bg-muted/60 px-2.5 py-1 text-sm">
                    <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="max-w-[140px] truncate">{selectedUser.name}</span>
                    <button
                      className="ml-0.5 text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setSelectedUser(null);
                        setUserSearch("");
                        setDebouncedUserSearch("");
                      }}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                    <Input
                      placeholder="Search user…"
                      value={userSearch}
                      onChange={e => {
                        setUserSearch(e.target.value);
                        setShowUserDropdown(true);
                      }}
                      onFocus={() => userSearch.length >= 2 && setShowUserDropdown(true)}
                      className="pl-8 w-44 h-9"
                    />
                    {userSearchLoading && (
                      <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-muted-foreground" />
                    )}
                  </div>
                )}

                {/* Dropdown */}
                {showUserDropdown && !selectedUser && userCandidates.length > 0 && (
                  <div className="absolute z-50 top-full mt-1 left-0 w-64 rounded-md border bg-popover shadow-md py-1">
                    {userCandidates.map((u: any) => {
                      const name = `${u.firstName} ${u.lastName}`.trim();
                      return (
                        <button
                          key={u.id}
                          className="w-full text-left flex items-center gap-2.5 px-3 py-2 text-sm hover-elevate"
                          onMouseDown={e => {
                            e.preventDefault();
                            setSelectedUser({ id: u.id, name: name || u.email });
                            setUserSearch("");
                            setShowUserDropdown(false);
                          }}
                        >
                          <Avatar className="w-6 h-6 shrink-0">
                            {u.profilePictureUrl && <AvatarImage src={u.profilePictureUrl} />}
                            <AvatarFallback className="text-[10px]">
                              {(u.firstName?.[0] ?? "") + (u.lastName?.[0] ?? "")}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="truncate font-medium">{name || "–"}</p>
                            <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* No results hint */}
                {showUserDropdown && !selectedUser && debouncedUserSearch.length >= 2 && !userSearchLoading && userCandidates.length === 0 && (
                  <div className="absolute z-50 top-full mt-1 left-0 w-64 rounded-md border bg-popover shadow-md px-3 py-2 text-sm text-muted-foreground">
                    No users found.
                  </div>
                )}
              </div>

              <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={timePeriod} onValueChange={v => setTimePeriod(v as TimePeriod)}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_PERIODS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          {isLoading ? (
            <div className="py-12 text-center text-muted-foreground">
              <RefreshCw className="w-6 h-6 mx-auto mb-3 animate-spin opacity-50" />
              Loading activity...
            </div>
          ) : streamEntries.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Activity className="w-10 h-10 mx-auto mb-3 opacity-20" />
              No events found for the selected filter.
            </div>
          ) : (
            <div className="divide-y divide-border -mx-6 px-6">
              {streamEntries.map((entry, i) =>
                entry.kind === "single" ? (
                  <SingleRow key={entry.event.id} event={entry.event} />
                ) : (
                  <QuizGroupRow key={entry.groupKey} group={entry} />
                )
              )}
            </div>
          )}

          {dataUpdatedAt > 0 && (
            <p className="mt-4 text-xs text-muted-foreground text-right">
              Last updated {formatDistanceToNow(new Date(dataUpdatedAt), { addSuffix: true })}
              {autoRefresh && " · auto-refreshing every 15s"}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
